import React from 'react';
import PropTypes from 'prop-types';

import TypeAhead from './typeahead';
import { modelsResponseProcessor } from './response-processors';
import { makeOptionRenderer, modelOptionRenderer } from './option-renderers';

const DEFAULT_LABELS = {
  bodyMake: '',
  bodyModel: '',
  chassisMake: '',
  chassisModel: '',
};

const DEFAULT_PLACEHOLDERS = {
  bodyMake: 'Enter make',
  bodyModel: 'Enter model',
  chassisMake: 'Enter chassis make',
  chassisModel: 'Enter chassis model',
};

const DEFAULT_PRE_LOAD_HELP = {
  bodyMake: 'Type to search for makes',
  bodyModel: 'Type to search for models',
  chassisMake: 'Type to search for chassis makes',
  chassisModel: 'Type to search for chassis models',
};

const DEFAULT_TEST_IDS = {
  bodyMake: 'make',
  bodyModel: 'model',
  chassisMake: 'chassisMake',
  chassisModel: 'chassisModel',
};

const errorFor = (errors, field, dirtyFields) =>
  dirtyFields?.includes(field)
    ? errors?.find((e) => e.field === field)?.message
    : undefined;

const MakeModelTypeAheadGroup = ({
  state,
  showChassis = false,
  requiresChassis = true,
  disablePaste = false,
  fieldWrapperClassName = '',
  renderFieldHeader = null,
  labels = DEFAULT_LABELS,
  placeholders = DEFAULT_PLACEHOLDERS,
  preLoadHelpMessages = DEFAULT_PRE_LOAD_HELP,
  testIds = DEFAULT_TEST_IDS,
}) => {
  const {
    bodyMake,
    setBodyMake,
    bodyMakeInputValue,
    setBodyMakeInputValue,
    bodyMakeOptions,
    setBodyMakeOptions,

    bodyModel,
    setBodyModel,
    bodyModelInputValue,
    setBodyModelInputValue,
    bodyModelOptions,
    setBodyModelOptions,

    chassisMake,
    setChassisMake,
    chassisMakeInputValue,
    setChassisMakeInputValue,
    chassisMakeOptions,
    setChassisMakeOptions,

    chassisModel,
    setChassisModel,
    chassisModelInputValue,
    setChassisModelInputValue,
    chassisModelOptions,
    setChassisModelOptions,

    fetchMakes,
    fetchModels,

    getErrors,
    dirtyFields,
    setDirtyFields,
  } = state;

  const errors = getErrors();

  // Changing the make ALWAYS clears the dependent model (input value,
  // selected option, and the option list). Model year is intentionally
  // left untouched.
  //
  // The `v !== current` guard is load-bearing: when the panel mounts with
  // existing data, TypeAhead's debounced fetch resolves and the auto-match
  // path calls setInputValue with the same string we already have. Without
  // the guard, that re-confirmation would wipe the pre-populated model.
  const handleBodyMakeInputChange = (v) => {
    setBodyMakeInputValue(v);
    if (v !== bodyMakeInputValue && (bodyModel || bodyModelInputValue)) {
      setBodyModel(undefined);
      setBodyModelInputValue('');
      setBodyModelOptions([]);
    }
  };

  const handleChassisMakeInputChange = (v) => {
    setChassisMakeInputValue(v);
    if (v !== chassisMakeInputValue && (chassisModel || chassisModelInputValue)) {
      setChassisModel(undefined);
      setChassisModelInputValue('');
      setChassisModelOptions([]);
    }
  };

  const markDirty = (field) =>
    setDirtyFields((prev) => [...prev, field]);

  const renderField = (field, child) => {
    const inner = (
      <>
        {renderFieldHeader ? renderFieldHeader(field) : null}
        {child}
      </>
    );
    return fieldWrapperClassName ? (
      <div key={field} className={fieldWrapperClassName}>
        {inner}
      </div>
    ) : (
      <React.Fragment key={field}>{inner}</React.Fragment>
    );
  };

  return (
    <>
      {renderField(
        'bodyMake',
        <TypeAhead
          disablePaste={disablePaste}
          testId={testIds.bodyMake}
          label={labels.bodyMake}
          placeholder={placeholders.bodyMake}
          preLoadHelpMsg={preLoadHelpMessages.bodyMake}
          required
          options={bodyMakeOptions || []}
          setOptions={setBodyMakeOptions}
          selectedOption={bodyMake}
          inputValue={bodyMakeInputValue}
          setInputValue={handleBodyMakeInputChange}
          setSelectedOption={setBodyMake}
          fetchOptions={fetchMakes}
          renderOptionLabel={makeOptionRenderer}
          onBlur={() => markDirty('bodyMake')}
          errorMessage={errorFor(errors, 'bodyMake', dirtyFields)}
        />,
      )}
      {renderField(
        'bodyModel',
        <TypeAhead
          disablePaste={disablePaste}
          testId={testIds.bodyModel}
          label={labels.bodyModel}
          placeholder={placeholders.bodyModel}
          preLoadHelpMsg={preLoadHelpMessages.bodyModel}
          required
          options={bodyModelOptions || []}
          setOptions={setBodyModelOptions}
          selectedOption={bodyModel}
          inputValue={bodyModelInputValue}
          setInputValue={setBodyModelInputValue}
          setSelectedOption={setBodyModel}
          fetchOptions={(inputValue, signal) =>
            fetchModels(
              inputValue,
              bodyMake?.value ? parseInt(bodyMake?.value, 10) : -1,
              signal,
            )
          }
          renderOptionLabel={modelOptionRenderer}
          responseProcessor={modelsResponseProcessor}
          onBlur={() => markDirty('bodyModel')}
          errorMessage={errorFor(errors, 'bodyModel', dirtyFields)}
        />,
      )}
      {showChassis &&
        renderField(
          'chassisMake',
          <TypeAhead
            disablePaste={disablePaste}
            testId={testIds.chassisMake}
            label={labels.chassisMake}
            placeholder={placeholders.chassisMake}
            preLoadHelpMsg={preLoadHelpMessages.chassisMake}
            required={requiresChassis}
            options={chassisMakeOptions || []}
            setOptions={setChassisMakeOptions}
            selectedOption={chassisMake}
            inputValue={chassisMakeInputValue}
            setInputValue={handleChassisMakeInputChange}
            setSelectedOption={setChassisMake}
            fetchOptions={fetchMakes}
            renderOptionLabel={makeOptionRenderer}
            onBlur={() => markDirty('chassisMake')}
            errorMessage={errorFor(errors, 'chassisMake', dirtyFields)}
          />,
        )}
      {showChassis &&
        renderField(
          'chassisModel',
          <TypeAhead
            disablePaste={disablePaste}
            testId={testIds.chassisModel}
            label={labels.chassisModel}
            placeholder={placeholders.chassisModel}
            preLoadHelpMsg={preLoadHelpMessages.chassisModel}
            required={requiresChassis}
            options={chassisModelOptions || []}
            setOptions={setChassisModelOptions}
            selectedOption={chassisModel}
            inputValue={chassisModelInputValue}
            setInputValue={setChassisModelInputValue}
            setSelectedOption={setChassisModel}
            fetchOptions={(inputValue, signal) =>
              fetchModels(
                inputValue,
                chassisMake?.value ? parseInt(chassisMake?.value, 10) : -1,
                signal,
              )
            }
            renderOptionLabel={modelOptionRenderer}
            responseProcessor={modelsResponseProcessor}
            onBlur={() => markDirty('chassisModel')}
            errorMessage={errorFor(errors, 'chassisModel', dirtyFields)}
          />,
        )}
    </>
  );
};

MakeModelTypeAheadGroup.propTypes = {
  state: PropTypes.shape({
    bodyMake: PropTypes.object,
    setBodyMake: PropTypes.func.isRequired,
    bodyMakeInputValue: PropTypes.string,
    setBodyMakeInputValue: PropTypes.func.isRequired,
    bodyMakeOptions: PropTypes.array,
    setBodyMakeOptions: PropTypes.func.isRequired,

    bodyModel: PropTypes.object,
    setBodyModel: PropTypes.func.isRequired,
    bodyModelInputValue: PropTypes.string,
    setBodyModelInputValue: PropTypes.func.isRequired,
    bodyModelOptions: PropTypes.array,
    setBodyModelOptions: PropTypes.func.isRequired,

    chassisMake: PropTypes.object,
    setChassisMake: PropTypes.func,
    chassisMakeInputValue: PropTypes.string,
    setChassisMakeInputValue: PropTypes.func,
    chassisMakeOptions: PropTypes.array,
    setChassisMakeOptions: PropTypes.func,

    chassisModel: PropTypes.object,
    setChassisModel: PropTypes.func,
    chassisModelInputValue: PropTypes.string,
    setChassisModelInputValue: PropTypes.func,
    chassisModelOptions: PropTypes.array,
    setChassisModelOptions: PropTypes.func,

    fetchMakes: PropTypes.func.isRequired,
    fetchModels: PropTypes.func.isRequired,

    getErrors: PropTypes.func.isRequired,
    dirtyFields: PropTypes.array,
    setDirtyFields: PropTypes.func.isRequired,
  }).isRequired,
  showChassis: PropTypes.bool,
  requiresChassis: PropTypes.bool,
  disablePaste: PropTypes.bool,
  fieldWrapperClassName: PropTypes.string,
  renderFieldHeader: PropTypes.func,
  labels: PropTypes.shape({
    bodyMake: PropTypes.string,
    bodyModel: PropTypes.string,
    chassisMake: PropTypes.string,
    chassisModel: PropTypes.string,
  }),
  placeholders: PropTypes.shape({
    bodyMake: PropTypes.string,
    bodyModel: PropTypes.string,
    chassisMake: PropTypes.string,
    chassisModel: PropTypes.string,
  }),
  preLoadHelpMessages: PropTypes.shape({
    bodyMake: PropTypes.string,
    bodyModel: PropTypes.string,
    chassisMake: PropTypes.string,
    chassisModel: PropTypes.string,
  }),
  testIds: PropTypes.shape({
    bodyMake: PropTypes.string,
    bodyModel: PropTypes.string,
    chassisMake: PropTypes.string,
    chassisModel: PropTypes.string,
  }),
};

export default MakeModelTypeAheadGroup;
